import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoLoader } from './LogoLoader';

// LoadingOverlay is the system-wide "we're working on it" gate. Wraps the
// animated LogoLoader in a native <dialog> so we get the browser's built-in
// focus trap, escape-to-close semantics (we suppress the close to keep the
// gate sticky), and a proper backdrop without rolling our own portal stack.
//
// Use cases (none of them are wired up by default — callers opt in):
//   - Onboarding submit (createOrg + bulkImport: ~3-8s)
//   - Bulk member import (large CSVs may take 5-30s server-side)
//   - GDPR export prep (when we ship Decision 6's erasure flow)
//   - AI plan generation in the command bar (Decision 5)
//
// API: a controlled <Dialog>-style component. Pass `open` and (optional)
// `caption`. Caller manages the open state — the overlay never closes
// itself, by design: a long process that completes early simply causes
// `open` to flip back to false from the parent.
//
// Accessibility:
//   - role="dialog" + aria-modal="true" via <dialog>
//   - aria-busy="true" when open so AT users hear "busy" while it's up
//   - LogoLoader carries role="status" + aria-live="polite" so screen
//     readers announce the state change

export interface LoadingOverlayProps {
  open: boolean;
  // Optional caption shown beneath the logo. Translate at the call site.
  // When omitted, falls back to the generic common.loading key.
  caption?: string;
}

export function LoadingOverlay({ open, caption }: LoadingOverlayProps) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);

  // Suppress Escape closing the overlay. Long processes can't be cancelled
  // from this layer, so letting Esc dismiss the gate would leave the user
  // staring at a blank app while the background work continues.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const onCancel = (e: Event) => e.preventDefault();
    el.addEventListener('cancel', onCancel);
    return () => el.removeEventListener('cancel', onCancel);
  }, []);

  const label = caption ?? t('common.loading');

  return (
    <dialog ref={dialogRef} className="loading-overlay" aria-busy={open}>
      <div className="loading-overlay__panel">
        <LogoLoader label={label} size={280} />
        {caption && <p className="loading-overlay__caption">{caption}</p>}
      </div>
    </dialog>
  );
}
